import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../environments/environment';
import {
  ConnectivityService,
  NetworkOfflineError,
} from '../app/providers/connectivity.service';
import { AuthService } from '../services/auth.service';
import { getMatchingRefreshableJwtMessage } from '../services/auth-jwt-error';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly retryHeader = 'X-Auth-Retry';

  constructor(
    private authService: AuthService,
    private connectivityService: ConnectivityService
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (this.shouldSkip(req)) {
      return next.handle(req);
    }

    if (!this.connectivityService.isOnline) {
      return throwError(() => new NetworkOfflineError());
    }

    const authReq = this.withBearerToken(req);

    return next.handle(authReq).pipe(
      catchError((error: unknown) => this.handleAuthError(req, next, error))
    );
  }

  private handleAuthError(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    error: unknown
  ): Observable<HttpEvent<unknown>> {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }

    const jwtMessage = getMatchingRefreshableJwtMessage(error);

    if (!jwtMessage) {
      return throwError(() => error);
    }

    if (req.headers.has(AuthInterceptor.retryHeader)) {
      return throwError(() => error);
    }

    return from(this.authService.refreshAccessTokenForRetry()).pipe(
      switchMap(() => {
        const retryReq = this.withBearerToken(req).clone({
          setHeaders: { [AuthInterceptor.retryHeader]: 'true' },
        });
        return next.handle(retryReq).pipe(
          catchError((retryError: unknown) =>
            this.handleAuthError(retryReq, next, retryError)
          )
        );
      }),
      catchError((refreshError) => throwError(() => refreshError))
    );
  }

  private shouldSkip(req: HttpRequest<unknown>): boolean {
    const url = req.url;

    if (url.includes('/assets/i18n/')) {
      return true;
    }

    if (url.includes(environment.oauth2.tokenEndpoint)) {
      return true;
    }

    if (url.includes(environment.oauth2.logoutUri)) {
      return true;
    }

    return false;
  }

  private withBearerToken(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const token = this.authService.getAccessToken();

    if (!token || req.headers.has('Authorization')) {
      return req;
    }

    return req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }
}
